import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import config from "../../config.js";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import PdfButton from "../ui/PdfButton.jsx";
import QrScanner from "../ui/QrScanner.jsx";
import WorkingAsChip from "../../features/operatorSession/WorkingAsChip";
import LoadTruckModal from "../../features/shippingHub/LoadTruckModal";
import { useShippingSigningFlags } from "../../features/shippingHub/signingFlags";
import { useShippingHubFlag } from "../../features/shippingHub/hubFlag";
import { HUB_TABS, readHubUrl, writeHubUrl } from "../../features/shippingHub/hubUrl";
import {
  dueText, filterOrders, loadSoBoard, normalizeBoard, ordersInLane, resolveSearchCode, searchTarget,
} from "../../features/shippingHub/soBoard";
import {
  laneRowView, loadedMessage, loadManifestDocument, showSigningQr, startDeliveryCode, startPickup,
} from "../../features/shippingHub/hubActions";
import { absoluteUrl } from "../../features/signing/signRequestsApi";
import useShippingUi from "./shipping/useShippingUi";
import OrderCard, { NotReadyCard } from "./shipping/OrderCard.jsx";
import PackageGuideScreen from "./shipping/PackageGuideScreen.jsx";
import OutsourcingTab from "./shipping/OutsourcingTab.jsx";
import ShipByJobTab from "./shipping/ShipByJobTab.jsx";
import HistoryTab from "./shipping/HistoryTab.jsx";
import "../mobile.css";

// The Shipping hub on a phone (2026-10-29, Mike: "a full featured mobile optimized version"; reverses the
// 2026-10-08 decision that kept Shipping off the phone). The same places as the desktop hub
// (features/shippingHub/ShippingHub.jsx, ?tab=&so=&pallet= via hubUrl): Pack · Ready to ship · Ready for pickup ·
// Out for delivery from GET /shipping/so-board, then Outsourcing, Ship by job and History; an order's package guide
// opens in place (?so=). Every row action is the desktop's (features/shippingHub/hubActions); every "who is doing
// this" PIN is the Working-as session (./shipping/useShippingUi); labels print at the station GET /features names.
// Layout only.

const API_BASE = config.API_BASE_URL;
const EMPTY = normalizeBoard(null);
const EMPTY_TEXT = {
  pack: "Nothing ready to pack right now.",
  ready: "No sealed pallets waiting to leave.",
  pickup: "No pallets waiting for a customer.",
  out: "Nothing out on our truck.",
};

export default function ShippingScreen() {
  const ui = useShippingUi();
  const signing = useShippingSigningFlags();
  const hub = useShippingHubFlag();
  const [place, setPlace] = useState(() => readHubUrl(window.location.search));
  const [board, setBoard] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState(null); // {text, ok}
  const [busyId, setBusyId] = useState(null);
  const [showNotReady, setShowNotReady] = useState(false);
  const [loadOrder, setLoadOrder] = useState(null);
  const [docOffer, setDocOffer] = useState(null); // a manifest just handed over (Load truck)
  const [scanning, setScanning] = useState(false);
  const readSeq = useRef(0);
  const activeTabRef = useRef(null);

  const reload = useCallback(async () => {
    const seq = ++readSeq.current;
    setLoading(true);
    const r = await loadSoBoard(API_BASE);
    if (seq !== readSeq.current) return;
    setLoading(false);
    if (r.ok) { setBoard(r.board); setLoadError(""); } else setLoadError(r.message);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const onPop = () => setPlace(readHubUrl(window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  useEffect(() => { writeHubUrl(place); }, [place]);
  // the tab row scrolls sideways: keep the open tab in view (a link or Back can open one off-screen)
  useEffect(() => {
    const el = activeTabRef.current;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [place.tab, place.so]);

  const go = (next, { push = false } = {}) => {
    const p = { ...place, ...next };
    if (push) writeHubUrl(p, { push: true });
    setPlace(p);
  };
  const openTab = (tab) => {
    setMessage(null); setDocOffer(null);
    go({ tab, so: null, pallet: null });
    if (HUB_TABS.find((t) => t.id === tab)?.lane) reload();
  };
  const openGuide = (so, pallet = null) => { setDocOffer(null); go({ so, pallet }, { push: true }); };
  const closeGuide = () => { go({ so: null, pallet: null }); reload(); };

  // a typed or scanned PLT / BCN / SO / customer / PO: filter the lanes, or open that order's package guide
  const find = async (text) => {
    const t = searchTarget(board, text);
    if (t.filter !== undefined) return;
    if (t.open) { setQuery(""); openGuide(t.open.so, t.open.pallet); return; }
    const r = await resolveSearchCode(API_BASE, t.resolve);
    if (r.open) { setQuery(""); openGuide(r.open.so, r.open.pallet); return; }
    setMessage({ text: r.message, ok: false });
  };
  const onScan = (text) => { setScanning(false); setQuery(text); find(text); };

  const run = async (order, action) => {
    if (busyId != null) return;
    setMessage(null); setDocOffer(null);
    const m = await action({ apiBase: API_BASE, order, ui, signing, setBusy: (on) => setBusyId(on ? order.plan_id : null), reload });
    if (m) setMessage(m);
  };

  const closeLoadTruck = (result) => {
    const o = loadOrder;
    setLoadOrder(null);
    if (!result || !result.loaded) return;
    setMessage(loadedMessage(o));
    if (result.manifest) {
      setDocOffer({ url: absoluteUrl(API_BASE, result.manifest), filename: `Manifest_${o ? o.order_number : "order"}.pdf`,
        what: "Manifest with the signing QR (driver + customer copy)" });
    }
    reload();
  };

  const filterText = searchTarget(board, query).filter !== undefined ? query : "";
  const laneOrders = useMemo(() => {
    const out = {};
    ["pack", "ready", "pickup", "out", "not_ready"].forEach((l) => { out[l] = filterOrders(ordersInLane(board, l), filterText); });
    return out;
  }, [board, filterText]);

  const tab = HUB_TABS.find((t) => t.id === place.tab) || HUB_TABS[0];
  const guideOrder = place.so ? board.orders.find((o) => Number(o.sales_order_id) === Number(place.so)) || null : null;

  // the hub's switch (GET /features shipping_hub) is the Shipping page's rollback: off, a phone gets no hub either
  if (hub.loaded && !hub.enabled) {
    return (
      <div className="jq-screen" data-testid="phone-shipping">
        <div className="jq-note jq-note-info" data-testid="phone-ship-hub-off">
          The Shipping hub is switched off. To ship from this phone, open More → Use desktop layout.
        </div>
      </div>
    );
  }

  if (place.so) {
    return (
      <div className="jq-screen" data-testid="phone-shipping">
        <PackageGuideScreen apiBase={API_BASE} salesOrderId={place.so} palletId={place.pallet} order={guideOrder}
          ui={ui} signing={signing} backLabel={`Back to ${tab.label}`} onClose={closeGuide} />
      </div>
    );
  }

  const renderLane = (lane) => {
    const rows = laneOrders[lane];
    if (loading && !board.orders.length) {
      return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;
    }
    if (!rows.length) {
      return <div className="jq-list-state">{filterText ? `Nothing here matches "${filterText}".` : EMPTY_TEXT[lane]}</div>;
    }
    return (
      <div data-testid={`phone-ship-lane-${lane}`}>
        {rows.map((o) => {
          const view = laneRowView(o, lane, signing);
          return (
            <OrderCard key={`${lane}-${o.plan_id}`} order={o} lane={lane} view={view}
              busy={busyId === o.plan_id} disabled={busyId != null}
              manifest={loadManifestDocument(API_BASE, o)}
              onPrimary={() => {
                if (view.act.kind === "pickup") run(o, startPickup);
                else if (view.act.kind === "load") { setMessage(null); setDocOffer(null); setLoadOrder(o); }
                else openGuide(o.sales_order_id, view.firstPalletId);
              }}
              onShowQr={() => run(o, view.needCode ? startDeliveryCode : showSigningQr)}
              onOpenGuide={() => openGuide(o.sales_order_id, view.firstPalletId)} />
          );
        })}
      </div>
    );
  };

  const notReady = laneOrders.not_ready;
  return (
    <div className="jq-screen" data-testid="phone-shipping">
      <div className="jq-stations" role="tablist" aria-label="Shipping">
        {HUB_TABS.map((t) => {
          const n = t.lane ? board.counts[t.id] : 0;
          return (
            <button key={t.id} type="button" role="tab" aria-selected={t.id === tab.id} ref={t.id === tab.id ? activeTabRef : undefined}
              className={`jq-station${t.id === tab.id ? " active" : ""}`} onClick={() => openTab(t.id)}>
              {t.label}{n > 0 && <span className="jq-queue-count">{n}</span>}
            </button>
          );
        })}
      </div>

      <div className="mb-2"><WorkingAsChip ui={ui} /></div>

      {tab.lane && (
        <div className="jq-list-tools">
          <form className="jq-search" role="search" onSubmit={(e) => { e.preventDefault(); find(query); }}>
            <i className="fas fa-search" aria-hidden="true" />
            <input type="search" enterKeyHint="search" className="form-control" data-own-scans="true"
              aria-label="Find an order or pallet" placeholder="PLT, BCN, SO, customer or PO"
              value={query} onChange={(e) => setQuery(e.target.value)} />
          </form>
          <button type="button" className="btn btn-outline-secondary jq-filter-btn" aria-label="Scan a label" onClick={() => setScanning(true)}>
            <i className="fas fa-qrcode" aria-hidden="true" />
          </button>
          <button type="button" className="btn btn-outline-secondary jq-filter-btn" aria-label="Refresh" disabled={loading} onClick={reload}>
            <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden="true" />
          </button>
        </div>
      )}

      {message && (
        <div className={`jq-note ${message.ok ? "jq-note-info" : "jq-note-danger"} d-flex justify-content-between align-items-start`} role="status">
          <span>{message.text}</span>
          <button type="button" className="btn-close ms-2" aria-label="Dismiss" onClick={() => setMessage(null)} />
        </div>
      )}
      {docOffer && (
        <div className="jq-note jq-note-info">
          <div className="mb-2">{docOffer.what}</div>
          <PdfButton url={docOffer.url} filename={docOffer.filename} label="Open the manifest" fetchNow className="btn btn-primary w-100" />
        </div>
      )}
      {loadError && tab.lane && (
        <div className="jq-note jq-note-danger" role="alert">
          Couldn't load the orders: {loadError}
          <button type="button" className="btn btn-outline-danger w-100 mt-2" onClick={reload}>Try again</button>
        </div>
      )}

      {tab.lane && renderLane(tab.id)}
      {tab.id === "pack" && notReady.length > 0 && !(loading && !board.orders.length) && (
        <div className="mt-3">
          <button type="button" className="btn btn-outline-secondary w-100 text-start" aria-expanded={showNotReady}
            onClick={() => setShowNotReady((v) => !v)}>
            {showNotReady ? "▾" : "▸"} Not ready to pack yet ({notReady.length})
          </button>
          {showNotReady && (
            <div className="mt-2" data-testid="phone-ship-lane-not_ready">
              {notReady.map((o) => (
                <NotReadyCard key={`nr-${o.plan_id}`} order={o} due={dueText(o.promised_date)} disabled={busyId != null}
                  onOpen={(palletId) => openGuide(o.sales_order_id, palletId)} onOutsourcing={() => openTab("outsourcing")} />
              ))}
            </div>
          )}
        </div>
      )}
      {tab.id === "outsourcing" && <OutsourcingTab apiBase={API_BASE} ui={ui} />}
      {tab.id === "byjob" && <ShipByJobTab apiBase={API_BASE} ui={ui} signing={signing} board={board} />}
      {tab.id === "history" && <HistoryTab apiBase={API_BASE} ui={ui} signing={signing} />}

      {loadOrder && (
        <LoadTruckModal apiBase={API_BASE} ui={ui} order={loadOrder} onClose={closeLoadTruck}
          onOpenGuide={() => { const o = loadOrder; setLoadOrder(null); openGuide(o.sales_order_id, null); }} />
      )}
      <FullScreenPanel open={scanning} title="Scan a label" onClose={() => setScanning(false)} testId="phone-ship-scan">
        {scanning && <QrScanner onScan={onScan} />}
      </FullScreenPanel>
    </div>
  );
}

